import {z} from 'zod';
import {REQUEST_STATUSES, requestStatusSchema, dispatchDaySchema, type RequestStatus} from './request';
import {openadrMessageSchema} from './openadr-message';
import {guardClusterSchema} from './mw-guard';
import {congestionAreaIdSchema, requestIdSchema, submissionIdSchema, userIdSchema} from './id';
import {STATUSES_IN_VIEW, type RequestView} from './request-view';

/**
 * One row of the overview: everything one wizard run created, however many congestion
 * areas it covered.
 *
 * The design draws five areas as `Breukelen +4` — one row, one status, one day — because
 * that is how the person who submitted it thinks about what they did. The requests
 * underneath stay separate in every other respect: each has its own events, its own
 * approval and its own detail screen.
 */
export const submissionMemberSchema = z.object({
  requestId: requestIdSchema,
  congestionAreaId: congestionAreaIdSchema,
  congestionAreaName: z.string(),
  status: requestStatusSchema,
});

/**
 * A member as the detail screen needs it: the area, where it stands, and the events it
 * holds. The profile is not here — it is the one thing the screen reads per area, and a
 * day of quarters per group for five areas is not a payload a page should carry.
 */
export const submissionMemberDetailSchema = submissionMemberSchema.extend({
  messages: z.array(openadrMessageSchema),
  /**
   * The cluster this area shares a guard with, or null when it is in none.
   *
   * The detail screen offers a cluster or one area and nothing wider: a cluster is the set
   * of areas a guard is measured across, so a total over two of them is a line nothing is
   * held to, and the guard drawn beside it would belong to neither.
   *
   * `guardClusterSchema` rather than a shape of its own, minus the `code` no screen shows.
   * The same cluster already travels on a preview and on a request's own guard record, and
   * three hand-written versions of it is how a branded id becomes a bare string and a
   * `nonnegative` guard becomes any number.
   *
   * The id and name are the cluster the area is in now; `guardMw` is the ceiling the
   * request was measured against, from its frozen guard record. A ceiling set or changed
   * since never reaches a request made before it.
   */
  cluster: guardClusterSchema.omit({code: true}).nullable(),
  /**
   * What this area asks for per quarter, and how much its groups had on offer — the two
   * numbers a combined diagram needs. Solvie computes the first, so it travels rather than
   * the profile it came from.
   */
  totals: z.array(z.object({quarter: z.coerce.date(), flexPower: z.number()})),
  availableFlexKw: z.number(),
  /**
   * What is asked of each flex group of this area: the energy its curve adds up to and how
   * long the dispatch runs. The two numbers the table shows per event, totalled here rather
   * than sent as curves — a cluster lists every one of its areas' groups at once, and a day of
   * quarters per group for five areas is the payload the profile is kept out of this
   * response to avoid.
   *
   * A group asked for nothing is not in the list: no event is built for it (`flex-event.ts`
   * skips a curve of zeroes), so there is no row for it to be — and no place in the count
   * the switcher shows before any event exists, which is this list's length.
   */
  groups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      energyKwh: z.number(),
      runningMs: z.number().int().nonnegative(),
    }),
  ),
});

export type SubmissionMemberDetail = z.infer<typeof submissionMemberDetailSchema>;

export type SubmissionMember = z.infer<typeof submissionMemberSchema>;

export const submissionSummarySchema = z.object({
  submissionId: submissionIdSchema,
  /** Shared by every member: the wizard asks for one day and applies it to every area. */
  dispatchDay: dispatchDaySchema,
  /** One status for the row, worked out by `aggregateStatus` from the members'. */
  status: requestStatusSchema,
  /** The most recent change among the members, which is what the row's timestamp means. */
  statusChangedAt: z.coerce.date(),
  createdBy: userIdSchema,
  /**
   * Whatever `app_user.name` holds — for a deleted account, what `anonymousIdentityOf` left
   * behind, because deletion renames rather than removes (ADR 0018).
   *
   * Not `.min(1)`: an empty name is a blank cell, and refusing to parse it would cost the
   * reader the whole page over one row's missing word.
   */
  createdByName: z.string(),
  /**
   * The congestion windows the submission asked about, as instants. Read out of the first
   * member's stored profile — the wizard applies one set of windows to every area — so a
   * row can draw them without the whole profile travelling with the list.
   */
  windows: z.array(
    z.object({
      start: z.coerce.date(),
      end: z.coerce.date(),
      /**
       * What share of the available flexibility was asked for, as the chip prints it.
       *
       * Optional, and absent rather than `0` when the stored profile does not carry it: a
       * chip reading `0%` says nothing was asked for, which is a different claim from not
       * knowing what was.
       */
      percentFlex: z.number().optional(),
    }),
  ),
  members: z.array(submissionMemberSchema).min(1),
});

export type SubmissionSummary = z.infer<typeof submissionSummarySchema>;

/**
 * Which view a status belongs to, inverted from `STATUSES_IN_VIEW` so a lookup is one map
 * rather than a scan of five arrays.
 */
const VIEW_OF_STATUS = new Map<RequestStatus, RequestView>(
  Object.entries(STATUSES_IN_VIEW).flatMap(([view, statuses]) =>
    statuses.map(status => [status, view as RequestView] as const),
  ),
);

/**
 * Most urgent first. A row says what the reader has to do about it, so a submission with
 * one area waiting for approval says "action required" even when the other four are done
 * — the opposite reading would hide work behind a majority.
 */
const VIEW_URGENCY: readonly RequestView[] = ['ACTION_REQUIRED', 'IN_PROGRESS', 'ACKNOWLEDGED', 'REJECTED'];

/**
 * Every status in the order a row prefers it: by view first, then by how far along the
 * state machine it is. The whole aggregation rule is this one list — the status a row shows
 * is the earliest of its members in it, and which chip the row belongs under follows from
 * that status.
 *
 * Exported because the database has to agree. A submission is filtered and counted by its
 * aggregate, and `min(rank)` over this order is the same answer in SQL as `[0]` is here: a
 * chip saying three beside a list showing four is worse than no chip at all
 * (`docs/audit/2026-09-flow-audit.md`, D3).
 */
const urgencyOf = (status: RequestStatus): number => VIEW_URGENCY.indexOf(VIEW_OF_STATUS.get(status) ?? 'REJECTED');

export const STATUS_BY_URGENCY: readonly RequestStatus[] = [...REQUEST_STATUSES].sort((left, right) => {
  const byView = urgencyOf(left) - urgencyOf(right);
  return byView !== 0 ? byView : REQUEST_STATUSES.indexOf(left) - REQUEST_STATUSES.indexOf(right);
});

/**
 * The one status a row shows for several requests: the earliest of them in
 * `STATUS_BY_URGENCY`.
 *
 * Decided by view before status, so a row that lands under "action required" carries a
 * status that chip covers — otherwise the row and the chip that produced it would disagree.
 * Within a view the earliest status in `REQUEST_STATUSES` wins, which is the order the state
 * machine moves through: the least far along is the one still needing something.
 */
export const aggregateStatus = (statuses: readonly RequestStatus[]): RequestStatus => {
  let best: RequestStatus | undefined;
  for (const status of statuses) {
    if (!best || STATUS_BY_URGENCY.indexOf(status) < STATUS_BY_URGENCY.indexOf(best)) best = status;
  }

  // Callers hold at least one member; the fallback keeps the type honest rather than throwing.
  return best ?? 'WAITING_FOR_APPROVAL';
};
